import { useMemo, useState } from "react";
import type { ReviewAnnotation } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { CATEGORY_LABELS, TYPE_ICONS, TYPE_LABELS } from "./constants.js";

type Filter = "all" | "unresolved" | "resolved" | "highlight" | "comment" | "strike" | "flag";

interface Props {
  annotations: ReviewAnnotation[];
  activeId: string | null;
  onJump: (a: ReviewAnnotation) => void;
  onResolve: (id: string, resolved: boolean) => void;
  onDelete: (id: string) => void;
  onEditNote: (id: string, note: string) => void;
  onAddSceneNote: () => void;
  onClose: () => void;
}

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unresolved", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "comment", label: "Comments" },
  { key: "highlight", label: "Highlights" },
  { key: "flag", label: "Flags" }
];

export function ReviewPanel({ annotations, activeId, onJump, onResolve, onDelete, onEditNote, onAddSceneNote, onClose }: Props) {
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const visible = useMemo(() => {
    return annotations.filter((a) => {
      if (filter === "all") return a.status !== "archived";
      if (filter === "unresolved") return a.status === "unresolved";
      if (filter === "resolved") return a.status === "resolved";
      return a.type === filter;
    });
  }, [annotations, filter]);

  // Resolved annotations are hidden by default (within "all"/type views).
  const shown = filter === "resolved" ? visible : visible.filter((a) => a.status !== "resolved");
  const resolvedHidden = filter !== "resolved" ? visible.filter((a) => a.status === "resolved").length : 0;

  return (
    <aside className="review-panel">
      <div className="review-panel-head">
        <h3>Review</h3>
        <div className="row gap-1">
          <button className="btn btn-icon btn-ghost btn-sm" title="Add a scene note" onClick={onAddSceneNote}>
            <Icon name="plus" size={15} />
          </button>
          <button className="btn btn-icon btn-ghost btn-sm" title="Hide panel" onClick={onClose}>
            <Icon name="panelRight" size={15} />
          </button>
        </div>
      </div>

      <div className="review-panel-filters">
        {FILTERS.map((f) => (
          <button key={f.key} className={`review-filter${filter === f.key ? " active" : ""}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="review-panel-list">
        {shown.length === 0 ? (
          <div className="review-empty faint">
            {annotations.length === 0
              ? "Select text in the manuscript to highlight, comment, strike, or flag it."
              : "Nothing here. Try a different filter."}
          </div>
        ) : (
          shown.map((a) => (
            <div key={a.id} className={`review-item${a.id === activeId ? " active" : ""}${a.status === "resolved" ? " resolved" : ""}`}>
              <button className="review-item-main" onClick={() => onJump(a)}>
                <div className="review-item-top">
                  <Icon name={TYPE_ICONS[a.type]} size={13} />
                  <span className="review-item-type">{TYPE_LABELS[a.type]}</span>
                  {a.category && <span className="review-item-cat">{CATEGORY_LABELS[a.category]}</span>}
                  {a.orphaned && <span className="review-item-orphan" title="The anchored text changed — relink needed">relink</span>}
                </div>
                {a.selectedText && <div className="review-item-quote truncate-2">“{a.selectedText}”</div>}
                {a.note && editing !== a.id && <div className="review-item-note">{a.note}</div>}
              </button>

              {editing === a.id ? (
                <div className="review-item-edit">
                  <textarea className="input" rows={3} value={draft} autoFocus
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { onEditNote(a.id, draft); setEditing(null); } }} />
                  <div className="row gap-1" style={{ justifyContent: "flex-end" }}>
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" onClick={() => { onEditNote(a.id, draft); setEditing(null); }}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="review-item-actions">
                  <button className="review-act" title={a.status === "resolved" ? "Reopen" : "Resolve"}
                    onClick={() => onResolve(a.id, a.status !== "resolved")}>
                    <Icon name="check" size={13} /> {a.status === "resolved" ? "Reopen" : "Resolve"}
                  </button>
                  <button className="review-act" title="Edit note" onClick={() => { setEditing(a.id); setDraft(a.note ?? ""); }}>
                    <Icon name="edit" size={13} />
                  </button>
                  <button className="review-act danger" title="Delete" onClick={() => onDelete(a.id)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {resolvedHidden > 0 && (
          <button className="review-show-resolved" onClick={() => setFilter("resolved")}>
            {resolvedHidden} resolved hidden — show
          </button>
        )}
      </div>
    </aside>
  );
}
